/**
 * inventoryService.ts — MADE-074
 *
 * Service layer for all Inventory API calls.
 * Extracted from Inventory.tsx to separate data-fetching from UI.
 * No logic changes — pure extraction.
 */

import { authFetch } from '../utils/authFetch';
import type { Material, InventoryReport, BuyList, TransitOrder } from '../types/madebb.types';

// ---------------------------------------------------------------------------
// Tenant parameterization (OS-INV-04)
//
// Every service function accepts an optional `apiBase` as its LAST argument,
// defaulting to '/api/madebb'. Existing Made.bb callers (and the unit tests)
// pass no apiBase, so URLs resolve to '/api/madebb/...' — byte-for-byte
// unchanged. Offshore callers pass '/api/os' (from useTenant().apiBase) so all
// data calls hit the tenant-scoped backend.
// ---------------------------------------------------------------------------
const DEFAULT_API_BASE = '/api/madebb';

// ---------------------------------------------------------------------------
// Request/response types
// ---------------------------------------------------------------------------

export interface TransitOrderRequest {
  materialId: string;
  quantityOrdered: number;
  costPerUnit: number;
  expectedDeliveryDate: string;
  supplier?: string | null;
  notes?: string | null;
}

export interface ReceiveOrderRequest {
  additionalFees: number;
  notes?: string | null;
}

export interface ReceiveOrderResult {
  success: boolean;
  newStock: number;
}

export interface BomUsageResult {
  usedIn: Array<{ id: string; name: string; type: string }>;
}

export interface VariantRequest {
  id: string;
  name: string;
  type: string;
  color: string;
  unit: string;
  parentMaterialId: string;
  isMaster: boolean;
  variantType: string;
  variantValue: string;
  currentStock: number;
  minStock: number;
  reorderQuantity: number;
  costPerYard?: number;
  costPerUnit?: number;
  supplier?: string;
  buyOrProduce: string;
  active: boolean;
  notes?: string;
}

export interface ReplicateRequest {
  sourceMaterialIds: string[];
  oldVariantId: string;
  newVariantId: string;
}

export interface ReplicateResult {
  success: boolean;
  created: string[];
}

export interface GenerateVariantsResult {
  variantCount: number;
  variants?: any[];
  [key: string]: any;
}

export interface CascadeResult {
  success: boolean;
  cascadeCount: number;
  [key: string]: any;
}

// ---------------------------------------------------------------------------
// Service methods
// ---------------------------------------------------------------------------

/** Load all materials from the API */
/** MADE-267: optional shopVisible filter — true/false/undefined (omit param) */
export async function loadMaterials(
  apiBase: string = DEFAULT_API_BASE,
  shopVisible?: boolean | null,
): Promise<Material[]> {
  const params = new URLSearchParams();
  if (shopVisible === true)  params.set('shopVisible', 'true');
  if (shopVisible === false) params.set('shopVisible', 'false');
  const qs = params.size ? '?' + params.toString() : '';
  const response = await authFetch(`${apiBase}/materials${qs}`);
  const data = await response.json();
  if (!data.success) throw new Error('Failed to load materials');
  return data.materials;
}

/** Create a new material */
export async function createMaterial(material: Material, apiBase: string = DEFAULT_API_BASE): Promise<Material> {
  const response = await authFetch(`${apiBase}/materials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(material),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to create material');
  return data.material;
}

/** MADE-269: Full API response type from updateMaterial */
export interface UpdateMaterialResponse {
  material: Material;
  /** MADE-269: suggested colour inferred from BOM (null if none or ambiguous) */
  suggested_color?: string | null;
  /** MADE-269: multiple colours found in BOM — staff must choose */
  ambiguous_colors?: string[] | null;
}

/** Update an existing material — returns full API response including BOM colour hints */
export async function updateMaterial(id: string, material: Material, apiBase: string = DEFAULT_API_BASE): Promise<UpdateMaterialResponse> {
  const response = await authFetch(`${apiBase}/materials/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(material),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to update material');
  return data; // { material, suggested_color, ambiguous_colors }
}

export const updateMaterialShopVisible = async (materialId: string, shopVisible: boolean, apiBase: string = DEFAULT_API_BASE): Promise<Material> => {
  const response = await authFetch(`${apiBase}/materials/${materialId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shopVisible })
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to update shop visibility');
  }
  const data = await response.json();
  return data.material;
};

/** Delete a material by ID */
export async function deleteMaterial(id: string, apiBase: string = DEFAULT_API_BASE): Promise<void> {
  const response = await authFetch(`${apiBase}/materials/${id}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to delete material');
}

/** Fetch inventory financial report */
export async function getInventoryReport(apiBase: string = DEFAULT_API_BASE): Promise<InventoryReport> {
  const response = await authFetch(`${apiBase}/inventory/report`);
  const data = await response.json();
  if (!data.success) throw new Error('Failed to generate report');
  return data.report;
}

/** Fetch buy list (materials that need to be ordered) */
export async function getBuyList(apiBase: string = DEFAULT_API_BASE): Promise<BuyList> {
  const response = await authFetch(`${apiBase}/inventory/buy-list`);
  const data = await response.json();
  if (!data.success) throw new Error('Failed to generate buy list');
  return data.buyList;
}

/** Place a transit (purchase) order for a material */
export async function placeTransitOrder(order: TransitOrderRequest, apiBase: string = DEFAULT_API_BASE): Promise<TransitOrder> {
  const response = await authFetch(`${apiBase}/inventory/transit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(order),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to place order');
  return data.transitOrder;
}

/** Get all in-transit orders */
export async function getTransitOrders(apiBase: string = DEFAULT_API_BASE): Promise<TransitOrder[]> {
  const response = await authFetch(`${apiBase}/inventory/transit`);
  const data = await response.json();
  if (!data.success) throw new Error('Failed to load transit orders');
  return data.transitOrders;
}

/** Receive a transit order (marks it as delivered, updates stock) */
export async function receiveTransitOrder(
  transitId: string,
  form: ReceiveOrderRequest,
  apiBase: string = DEFAULT_API_BASE
): Promise<ReceiveOrderResult> {
  const response = await authFetch(`${apiBase}/inventory/transit/${transitId}/receive`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      additionalFees: form.additionalFees ?? 0,
      notes: form.notes ?? null,
    }),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to receive order');
  return { success: true, newStock: data.newStock };
}

/** Get BOM usage for a material (which manufactured items use it) */
export async function getBomUsage(materialId: string, apiBase: string = DEFAULT_API_BASE): Promise<BomUsageResult> {
  const response = await authFetch(`${apiBase}/materials/${materialId}/bom-usage`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to get BOM usage');
  return { usedIn: data.usedIn || [] };
}

/** Create a color variant material */
export async function createVariant(variant: VariantRequest, apiBase: string = DEFAULT_API_BASE): Promise<Material> {
  const response = await authFetch(`${apiBase}/materials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(variant),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to create variant');
  return data.material;
}

/** Replicate BOM items from one variant to another */
export async function replicateWithVariant(request: ReplicateRequest, apiBase: string = DEFAULT_API_BASE): Promise<ReplicateResult> {
  const response = await authFetch(`${apiBase}/materials/replicate-with-variant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to replicate BOM items');
  return { success: true, created: data.created || [] };
}

/** Generate variants from BOM analysis (preview or confirm) */
export async function generateVariants(
  materialId: string,
  confirm: boolean,
  apiBase: string = DEFAULT_API_BASE
): Promise<GenerateVariantsResult> {
  const response = await authFetch(`${apiBase}/materials/${materialId}/generate-variants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to generate variants');
  return data;
}

/** Cascade variant to manufactured items (preview or confirm) */
/** MADE-270: Extended cascade params */
export interface CascadeParams {
  dryRun?: boolean;
  targetMaterialIds?: string[];
  /** Legacy param — backward compat */
  confirm?: boolean;
}

export async function cascadeToManufactured(
  materialId: string,
  confirmOrParams: boolean | CascadeParams,
  apiBase: string = DEFAULT_API_BASE
): Promise<CascadeResult> {
  // Accept both legacy boolean and new params object
  const body = typeof confirmOrParams === 'boolean'
    ? { confirm: confirmOrParams }
    : confirmOrParams;

  const response = await authFetch(`${apiBase}/materials/${materialId}/cascade-to-manufactured`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to cascade variants');
  return data;
}

/** Assign a parent material (convert standalone to variant) */
export async function assignParent(
  materialId: string,
  updatedMaterial: Material,
  apiBase: string = DEFAULT_API_BASE
): Promise<Material> {
  const response = await authFetch(`${apiBase}/materials/${materialId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updatedMaterial),
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to assign parent');
  return data.material;
}
